import type { Auth } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import type { Functions } from "firebase/functions";
import type { FirebaseStorage } from "firebase/storage";

/* ============================================================
   O que cada plataforma traz

   Este pacote é o mesmo código para o site e para o app, e por isso ele
   NÃO PODE importar nem o Firebase iniciado nem o enviador de foto: os
   dois existem dos dois lados, com implementações diferentes.

   - o Firebase: o site inicia com persistência de navegador; o app usa
     `initializeAuth` com AsyncStorage, cache em memória e long polling
     (é o que faz o Firestore funcionar no React Native);
   - o enviador: no site sobe um `Blob` do canvas; no app sobe um
     arquivo lido por `fetch` de um `file://`, porque o SDK não monta
     Blob de `Uint8Array` lá;
   - o aleatório do código de convite: `crypto.getRandomValues` não
     existe no React Native, onde quem responde é o `expo-crypto`.

   Então cada lado ENTREGA os seus, uma vez, na abertura. As ligações
   são `let` exportado de propósito — em ESM isso é ligação viva, então
   quem já importou `db` passa a enxergar o valor novo sem precisar
   chamar função nenhuma. É o que permitiu mover 1.800 linhas sem tocar
   numa única linha de tela.

   NADA AQUI É LIDO NO TOPO DE MÓDULO. Todo uso de `db` acontece dentro
   de função, então a ordem real é: os módulos carregam, a entrada do app
   chama `configurar`, e só depois alguma tela pede dado.
   ============================================================ */

/** Um arquivo pronto para subir, do jeito que cada plataforma o tem. */
export interface ParaSubir {
  caminho: string;
  dados: unknown;
}

export interface Progresso {
  /** 0 a 1 somando todos os arquivos do envio. */
  fracao: number;
  enviados: number;
  total: number;
}

export interface Ambiente {
  db: Firestore;
  storage: FirebaseStorage;
  auth: Auth;
  functions: Functions;
  /**
   * A extensão que as fotos deste lado salvam.
   *
   * O site salva WebP; o app salva WebP no Android e JPEG no iPhone,
   * porque o `SaveFormat.WEBP` do image-manipulator não vale lá. Ela
   * entra no caminho do arquivo no Storage, e quem lê usa o caminho
   * gravado no documento — nunca deduz a extensão.
   */
  extensao: string;
  subirTodos: (arquivos: ParaSubir[], aoProgredir?: (p: Progresso) => void) => Promise<void>;
  /** `crypto.getRandomValues` no site, `expo-crypto` no app. */
  bytesAleatorios: (quantos: number) => Uint8Array;
}

export let db: Firestore;
export let storage: FirebaseStorage;
export let auth: Auth;
export let functions: Functions;

let extras: Pick<Ambiente, "extensao" | "subirTodos" | "bytesAleatorios"> | null = null;

/**
 * O que não é instância do Firebase.
 *
 * Função e não ligação viva porque estes três são CHAMADOS, e uma
 * chamada de algo indefinido dá "undefined is not a function" — que não
 * diz onde faltou configurar. Aqui a mensagem diz.
 */
export function daPlataforma(): Pick<Ambiente, "extensao" | "subirTodos" | "bytesAleatorios"> {
  if (!extras) {
    throw new Error(
      "@nhomi/dados: `configurar()` não foi chamado. "
      + "Cada app chama uma vez na abertura — ver `lib/dados.ts`.",
    );
  }
  return extras;
}

export function configurar(ambiente: Ambiente): void {
  db = ambiente.db;
  storage = ambiente.storage;
  auth = ambiente.auth;
  functions = ambiente.functions;
  extras = {
    extensao: ambiente.extensao,
    subirTodos: ambiente.subirTodos,
    bytesAleatorios: ambiente.bytesAleatorios,
  };
}
